import { useEffect, useState, useCallback } from 'react';
import { FaComments, FaTimes, FaRobot, FaHeadset, FaCircle } from 'react-icons/fa';

// Tawk.to Configuration
const TAWK_PROPERTY_ID = '6a34717eacfa9d1d3e924adf';
const TAWK_WIDGET_ID = '1jredmmbo';

// Support Departments
const DEPARTMENTS = [
  { id: 'general', name: 'General Support' },
  { id: 'deposits', name: 'Deposit & Withdrawal' },
  { id: 'trading', name: 'Trading Assistance' },
  { id: 'technical', name: 'Technical Support' },
  { id: 'vip', name: 'VIP Support' }
];

// AI Assistant FAQs
const AI_FAQS = [
  { question: 'How do I deposit funds?', answer: 'Go to Profile → Deposit, select your coin and account, then send funds to the displayed wallet address. Your deposit will be credited after blockchain network confirmation.' },
  { question: 'How long do withdrawals take?', answer: 'Withdrawals are typically released to the blockchain network within 24-48 hours. You will see the status change from Pending to Completed in your Activity Log.' },
  { question: 'What is arbitrage trading?', answer: 'Arbitrage trading lets you allocate funds to automated strategies that profit from market price differences. Go to the Arbitrage section, choose a plan, and allocate your funds.' },
  { question: 'How do I complete KYC verification?', answer: 'Visit Profile → Security → Account Verification to submit your identity documents. Verification is usually completed within 24 hours.' },
  { question: 'What are the trading fees?', answer: 'Internal transfers between your accounts (Funding, Trading, Arbitrage) are completely free. Fees may apply only on external withdrawals and deposits depending on the network.' },
  { question: 'I forgot my password', answer: 'Click "Forgot Password" on the Sign In page and enter your registered email. A password reset link will be sent to your inbox.' },
  { question: 'Why is my balance not updated after deposit?', answer: 'Deposits require blockchain network confirmations before your balance is updated. This usually takes 24-48 hours depending on network congestion. Check your Activity Log for the current status.' },
  { question: 'My withdrawal is still pending', answer: 'Withdrawals are broadcast to the blockchain network. If pending for more than 48 hours, it may be due to network congestion. Check your Activity Log — the status will update to Completed once confirmed on-chain.' },
  { question: 'How do I transfer between accounts?', answer: 'Go to Profile → Transfer to move funds instantly between your Funding, Trading, and Arbitrage accounts. Internal transfers are instant and free of charge.' },
  { question: 'What coins are supported?', answer: 'We support USDT, USDC, BTC, ETH, XRP, and SOL. You can deposit, withdraw, and trade all supported coins from your Profile page.' },
  { question: 'How do I enable 2FA?', answer: 'Go to Profile → Security → Two-Factor Authentication to enable 2FA on your account. We strongly recommend enabling 2FA to protect your funds.' },
  { question: 'Why can\'t I log in to my account?', answer: 'Check that you are using the correct email and password. If you forgot your password, use the "Forgot Password" option on the Sign In page. Make sure your account is verified.' },
  { question: 'What is contract trading?', answer: 'Contract trading lets you trade crypto contracts with multiplied returns. Go to the Trade section, select a coin and contract duration, and place your trade.' },
  { question: 'How do I change my password?', answer: 'Go to Profile → Security → Password Controls to update your password. You will need to enter your current password before setting a new one.' },
  { question: 'Is my money safe?', answer: 'Yes. Client funds are held in segregated accounts with multi-signature protection. The platform uses AES-256 encryption, rate limiting, and continuous threat monitoring.' },
  { question: 'What is the minimum deposit amount?', answer: 'There is no fixed minimum deposit amount. However, we recommend depositing enough to cover any applicable network fees on your chosen coin.' },
  { question: 'Why was my withdrawal rejected?', answer: 'Withdrawals can be rejected if the destination address is invalid, the amount exceeds your available balance, or if there is a security concern. Check your Activity Log for the rejection status and try again.' },
  { question: 'How do I check my transaction history?', answer: 'Go to Profile → Transactions (Activity Log) to see all your deposits, withdrawals, transfers, and trading activity with their current status.' },
  { question: 'Can I cancel a pending withdrawal?', answer: 'Once a withdrawal is broadcast to the blockchain network it cannot be reversed. If your withdrawal is still in Pending status, contact us immediately via Telegram @GeminiAI_Support_Com before it is confirmed on-chain.' },
  { question: 'How long does account verification take?', answer: 'KYC verification is typically completed within 24 hours after you submit your documents. You will see your verification status update in Profile → Security.' },
  { question: 'What is the VIP plan?', answer: 'Our VIP plan gives you access to exclusive high-yield arbitrage strategies, priority withdrawal processing, dedicated account management, and higher deposit/withdrawal limits. VIP members receive premium support and early access to new trading features. To learn more or upgrade to VIP, contact us on Telegram @GeminiAI_Support_Com.' },
  { question: 'How do I upgrade to VIP?', answer: 'To upgrade to VIP status, contact our team directly:\n📱 Telegram: @GeminiAI_Support_Com\n📧 Email: geminiconcierge.net@gmail.com\n\nOur team will guide you through the VIP onboarding process and explain the exclusive benefits available to you.' },
  { question: 'What are VIP benefits?', answer: 'VIP members enjoy: higher arbitrage returns, priority withdrawal processing (within 12 hours), dedicated account manager, increased deposit/withdrawal limits, early access to new trading strategies, and exclusive market insights. Contact @GeminiAI_Support_Com on Telegram to get started.' },
  { question: 'How do I contact support?', answer: 'You can reach our support team through:\n📧 Email: geminiconcierge.net@gmail.com\n📱 Telegram: @GeminiAI_Support_Com\n\nFor fastest response, use Telegram. Our team is available and typically responds within 24 hours.' },
  { question: 'Where is your Telegram?', answer: 'Our official Telegram support is @GeminiAI_Support_Com. You can also reach us by email at geminiconcierge.net@gmail.com. Please only use official channels to protect your account security.' },
  { question: 'What is the support email?', answer: 'Our support email is geminiconcierge.net@gmail.com. You can also contact us faster on Telegram at @GeminiAI_Support_Com. Our support team responds within 24 hours.' }
];

export default function TawkToChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [showDepartmentSelect, setShowDepartmentSelect] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [supportStatus, setSupportStatus] = useState('online'); // online, away, offline
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiChatHistory, setAiChatHistory] = useState([
    { type: 'ai', message: 'Hi! I\'m Gemini AI Assistant. I can help you with any questions about deposits, withdrawals, trading, account issues, and more. Select a question below or type your own.' }
  ]);
  const [userInput, setUserInput] = useState('');

  // Load Tawk.to script
  useEffect(() => {
    // Check if already loaded
    if (window.Tawk_API && window.Tawk_LoadStart) {
      return;
    }

    // Inject Tawk.to script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    // Configure Tawk.to before loading
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Custom configuration
    window.Tawk_API.onLoad = function() {
      // Hide default widget button (we'll use custom)
      window.Tawk_API.hideWidget();
      
      // Set visitor attributes
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.email) {
        window.Tawk_API.setAttributes({
          name: user.displayName || user.email,
          email: user.email,
          phone: user.phone || ''
        }, function(error) {
          if (error) console.log('[Tawk.to] Error setting attributes:', error);
        });
      }
    };

    // Handle status changes
    window.Tawk_API.onStatusChange = function(status) {
      setSupportStatus(status); // online, away, offline
    };

    // Handle new messages
    window.Tawk_API.onChatMessageAgent = function(message) {
      setHasNewMessage(true);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound if enabled
      const soundEnabled = localStorage.getItem('chatSoundEnabled') !== 'false';
      if (soundEnabled) {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      }
    };

    // Handle chat minimized
    window.Tawk_API.onChatMinimized = function() {
      setIsOpen(false);
    };

    // Handle chat maximized
    window.Tawk_API.onChatMaximized = function() {
      setIsOpen(true);
      setHasNewMessage(false);
      setUnreadCount(0);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup - remove script
      const existingScript = document.querySelector(`script[src*="${TAWK_PROPERTY_ID}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Toggle chat
  const toggleChat = useCallback(() => {
    if (!isOpen) {
      // Opening chat
      if (selectedDepartment) {
        // Department already selected, open Tawk.to directly
        if (window.Tawk_API && window.Tawk_API.maximize) {
          window.Tawk_API.maximize();
        }
      } else {
        // Show department selection first
        setShowDepartmentSelect(true);
      }
    } else {
      // Closing chat
      if (window.Tawk_API && window.Tawk_API.minimize) {
        window.Tawk_API.minimize();
      }
      setIsOpen(false);
    }
  }, [isOpen, selectedDepartment]);

  // Select department
  const handleDepartmentSelect = useCallback((dept) => {
    setSelectedDepartment(dept);
    setShowDepartmentSelect(false);
    
    // Set department in Tawk.to
    if (window.Tawk_API && window.Tawk_API.setDepartment) {
      window.Tawk_API.setDepartment(dept.id, function(error) {
        if (error) console.log('[Tawk.to] Error setting department:', error);
      });
    }
    
    // Go straight to AI chat
    setIsOpen(true);
  }, []);

  // Choose AI Assistant
  const handleChooseAI = useCallback(() => {
    setShowAIAssistant(false);
    setIsOpen(true);
  }, []);

  // Choose Human Support
  const handleChooseHuman = useCallback(() => {
    setShowAIAssistant(false);
    if (window.Tawk_API && window.Tawk_API.maximize) {
      window.Tawk_API.maximize();
    }
    setIsOpen(true);
  }, []);

  // AI Chat handlers
  const handleAIQuestion = useCallback((question) => {
    // Add user question
    setAiChatHistory(prev => [...prev, { type: 'user', message: question }]);
    
    // Find matching FAQ by scoring keyword overlap
    const q = question.toLowerCase().replace(/[?!.,]/g, '');
    const qWords = new Set(q.split(' ').filter(w => w.length > 3));
    let bestFaq = null;
    let bestScore = 0;
    AI_FAQS.forEach(f => {
      const fWords = f.question.toLowerCase().replace(/[?!.,]/g, '').split(' ').filter(w => w.length > 3);
      const matches = fWords.filter(w => qWords.has(w)).length;
      if (matches > bestScore) { bestScore = matches; bestFaq = f; }
    });
    const faq = bestScore >= 2 ? bestFaq : null;
    
    const CONTACT_LINE = '\n\n📱 Telegram: @GeminiAI_Support_Com\n📧 Email: geminiconcierge.net@gmail.com';

    // Simulate AI thinking
    setTimeout(() => {
      if (faq) {
        setAiChatHistory(prev => [...prev, { type: 'ai', message: faq.answer + CONTACT_LINE }]);
      } else {
        setAiChatHistory(prev => [...prev, {
          type: 'ai',
          message: 'I\'m not sure about that, but our support team is ready to help you directly.' + CONTACT_LINE
        }]);
      }
    }, 500);
  }, []);

  // Handle custom user input in AI chat
  const handleSendMessage = useCallback(() => {
    if (!userInput.trim()) return;
    handleAIQuestion(userInput);
    setUserInput('');
  }, [userInput, handleAIQuestion]);

  // Get status color
  const getStatusColor = () => {
    switch (supportStatus) {
      case 'online': return 'bg-emerald-500';
      case 'away': return 'bg-amber-500';
      case 'offline': return 'bg-slate-500';
      default: return 'bg-emerald-500';
    }
  };

  // Get status text
  const getStatusText = () => {
    switch (supportStatus) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'offline': return 'Offline';
      default: return 'Online';
    }
  };

  return (
    <>
      {/* Hide Default Tawk.to Widget */}
      <style>{`
        .tawk-min-container,
        .tawk-chat-panel,
        .tawk-iframe-container,
        .tawk-branding,
        iframe[src*="tawk.to"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>
      
      {/* Floating Chat Button */}
      <div className="fixed bottom-24 right-4 md:right-8 z-50 flex flex-col items-end gap-2">
        {/* Pulse Animation Ring */}
        {hasNewMessage && (
          <span className="absolute -inset-1 rounded-full bg-blue-500/30 animate-ping"></span>
        )}
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
        
        {/* Main Chat Button */}
        <button
          onClick={toggleChat}
          className={`relative group flex items-center justify-center w-14 h-14 rounded-full shadow-lg shadow-blue-500/30 transition-all duration-300 hover:scale-110 hover:shadow-blue-500/50 ${
            isOpen 
              ? 'bg-slate-700 rotate-90' 
              : 'bg-gradient-to-r from-blue-600 to-cyan-600'
          } ${hasNewMessage ? 'animate-pulse' : ''}`}
        >
          {isOpen ? (
            <FaTimes className="w-6 h-6 text-white" />
          ) : (
            <>
              <FaComments className="w-6 h-6 text-white" />
              {/* Status Indicator */}
              <span className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-slate-900 ${getStatusColor()}`}></span>
            </>
          )}
        </button>
        
        {/* Tooltip */}
        <div className="hidden md:block absolute right-16 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {isOpen ? 'Close Chat' : 'Live Support'}
        </div>
      </div>

      {/* Department Selection Modal */}
      {showDepartmentSelect && (
        <div className="fixed bottom-24 right-4 md:right-24 w-[320px] md:w-[380px] bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold text-lg">How can we help?</h3>
                <p className="text-blue-100 text-xs mt-0.5">Select a department</p>
              </div>
              <button 
                onClick={() => setShowDepartmentSelect(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Department Options */}
          <div className="p-4 space-y-2">
            {DEPARTMENTS.map((dept) => (
              <button
                key={dept.id}
                onClick={() => handleDepartmentSelect(dept)}
                className="w-full flex items-center p-3 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-blue-500/30 transition-all text-left group"
              >
                <span className="text-slate-200 font-medium group-hover:text-white">
                  {dept.name}
                </span>
              </button>
            ))}
          </div>
          
          {/* Status Footer */}
          <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700/50">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span className={`w-2 h-2 rounded-full ${getStatusColor()}`}></span>
              <span>Support is {getStatusText()}</span>
            </div>
          </div>
        </div>
      )}


      {/* AI Chat Interface */}
      {isOpen && selectedDepartment && (
        <div className="fixed bottom-24 right-4 md:right-24 w-[320px] md:w-[380px] h-[480px] bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* AI Chat Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <FaRobot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Gemini AI</h3>
                <p className="text-purple-100 text-xs">Always ready to help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Chat History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {aiChatHistory.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.type === 'user' 
                    ? 'bg-blue-600 text-white rounded-br-md' 
                    : 'bg-slate-700 text-slate-200 rounded-bl-md'
                }`}>
                  <p className="text-sm">{msg.message}</p>
                </div>
              </div>
            ))}
            
            {/* Quick Questions */}
            {aiChatHistory.length === 1 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-slate-500 mb-2">Popular questions:</p>
                {AI_FAQS.map((faq, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAIQuestion(faq.question)}
                    className="w-full text-left p-2.5 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 hover:text-white text-xs transition-colors"
                  >
                    {faq.question}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Input Area */}
          <div className="p-4 bg-slate-800/50 border-t border-slate-700/50">
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your question..."
                className="flex-1 bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
              />
              <button
                onClick={handleSendMessage}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tawk.to iframe container (hidden, controlled via API) */}
      <div id="tawk-to-container" className="hidden" />
    </>
  );
}
